import { outline, tone } from "../paint";
import { RigModel, css, emptyBounds, isEmpty, mix, scale, shapeBounds, union, WHITE, type Bounds, type Color, type Form, type Palette, type Part, type PathOp, type Rig, type Shape } from "./Rig";

/**
 * Pixel art for rigs: a part painted once as a little picture at a fixed
 * pixel density (the tiles' own, for the crew), pixel-crisp and outlined
 * like the tiles, then moved about by the rig like any other shape. The
 * painter works in the part's own units (y up, from its pivot) through a
 * Pen, so a design reads the same as the rig around it.
 */

/** Is the point (in the part's units) inside the region? */
export type Inside = (x: number, y: number) => boolean;

export const disc =
  (cx: number, cy: number, rx: number, ry = rx): Inside =>
  (x, y) =>
    ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 <= 1;

export const box =
  (x0: number, y0: number, x1: number, y1: number): Inside =>
  (x, y) =>
    x >= x0 && x <= x1 && y >= y0 && y <= y1;

/** A round-ended bar `w` wide from (ax, ay) to (bx, by). */
export function cap(ax: number, ay: number, bx: number, by: number, w: number): Inside {
  const dx = bx - ax;
  const dy = by - ay;
  const l2 = dx * dx + dy * dy || 1e-9;
  return (x, y) => {
    const t = Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / l2));
    return (x - ax - t * dx) ** 2 + (y - ay - t * dy) ** 2 <= (w / 2) ** 2;
  };
}

/** A bar `w` wide through each of the points in turn. */
export function stroke(pts: readonly (readonly [number, number])[], w: number): Inside {
  const bits = pts.slice(1).map((p, i) => cap(pts[i]![0], pts[i]![1], p[0], p[1], w));
  return (x, y) => bits.some((b) => b(x, y));
}

export function poly(pts: readonly (readonly [number, number])[]): Inside {
  return (x, y) => {
    let inside = false;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      const [xi, yi] = pts[i]!;
      const [xj, yj] = pts[j]!;
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };
}

export const any =
  (...rs: Inside[]): Inside =>
  (x, y) =>
    rs.some((r) => r(x, y));

const hex = (c: Color) => `#${c.map((v) => Math.round(Math.max(0, Math.min(1, v)) * 255).toString(16).padStart(2, "0")).join("")}`;

/** Five tones, dark to light, round a colour: the ramp a region is shaded with. */
function rampOf(c: Color): string[] {
  return [hex(scale(c, 0.5)), hex(scale(c, 0.72)), hex(c), hex(mix(c, WHITE, 0.18)), hex(mix(c, WHITE, 0.36))];
}

/** A painter's view of one part's picture: regions filled flat or lit, in the part's units. */
export class Pen {
  private readonly ramps = new Map<string, string[]>();

  constructor(
    private readonly ctx: CanvasRenderingContext2D,
    private readonly box: Bounds,
    private readonly ppu: number,
    private readonly palette: Palette,
  ) {}

  private each(inside: Inside, color: (px: number, py: number, x: number, y: number) => string): void {
    const w = this.ctx.canvas.width;
    const h = this.ctx.canvas.height;
    for (let py = 0; py < h; py++) {
      for (let px = 0; px < w; px++) {
        const x = this.box.minX + (px + 0.5) / this.ppu;
        const y = this.box.maxY - (py + 0.5) / this.ppu;
        if (!inside(x, y)) continue;
        this.ctx.fillStyle = color(px, py, x, y);
        this.ctx.fillRect(px, py, 1, 1);
      }
    }
  }

  ramp(name: string): string[] {
    let r = this.ramps.get(name);
    if (!r) {
      const c = this.palette[name];
      if (!c) throw new Error(`rig: no colour ${name} in the palette`);
      this.ramps.set(name, (r = rampOf(c)));
    }
    return r;
  }

  /** A rig shape (see Rig.ts), in its own colour, flat (see-through as it says). */
  shape(s: Shape): void {
    const c = this.palette[s.color];
    if (!c) throw new Error(`rig: no colour ${s.color} in the palette`);
    const sb = shapeBounds(s);
    const inside = insideOf(s);
    const color = css(c);
    this.ctx.globalAlpha = s.alpha ?? 1;
    this.each((x, y) => x >= sb.minX && x <= sb.maxX && y >= sb.minY && y <= sb.maxY && inside(x, y), () => color);
    this.ctx.globalAlpha = 1;
  }

  /**
   * Light the whole picture from the upper left: a lighter pixel along its
   * top and left edges, darker along its bottom and right, a dithered band
   * of shade inside that.
   */
  shade(): void {
    const { width: w, height: h } = this.ctx.canvas;
    const img = this.ctx.getImageData(0, 0, w, h);
    const d = img.data;
    const src = d.slice();
    const solid = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && src[(y * w + x) * 4 + 3]! >= 128;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = (y * w + x) * 4;
        if (src[i + 3]! < 128) continue;
        let k = 1;
        if (!solid(x, y - 1) || !solid(x - 1, y)) k = 1.2;
        else if (!solid(x, y + 1) || !solid(x + 1, y)) k = 0.72;
        else if ((!solid(x + 2, y + 2) || !solid(x, y + 3)) && (x + y) % 2 === 0) k = 0.86;
        if (k !== 1) for (let c = 0; c < 3; c++) d[i + c] = Math.min(255, Math.round(src[i + c]! * k));
      }
    }
    this.ctx.putImageData(img, 0, 0);
  }

  /** The region in one flat colour (`tone` 0..4 down its ramp; 2 is the colour itself). */
  flat(name: string, inside: Inside, tone = 2): void {
    const c = this.ramp(name)[tone]!;
    this.each(inside, () => c);
  }

  /**
   * The region lit from the upper left: a light rim along its top and left,
   * shade along its bottom and right, dithered where the shade comes in.
   */
  lit(name: string, inside: Inside): void {
    const r = this.ramp(name);
    const d = 1.5 / this.ppu;
    this.each(inside, (px, py, x, y) => {
      if (!inside(x - d, y + d) || !inside(x, y + d)) return r[3]!;
      if (!inside(x + d, y - d)) return r[1]!;
      if (!inside(x + 3 * d, y - 3 * d)) return tone(r, 0.3, px, py);
      return r[2]!;
    });
  }
}

/** Each painted picture, by its form and its palette's colours. */
const painted = new WeakMap<Form, Map<string, HTMLCanvasElement>>();
/** A palette's colours as a key, worked out once a palette. */
const keys = new WeakMap<Palette, string>();
function keyOf(palette: Palette): string {
  let k = keys.get(palette);
  if (k === undefined) keys.set(palette, (k = JSON.stringify(palette)));
  return k;
}

/** A part's pixel art: `box` (in the part's units) painted at `ppu` pixels a unit by `paint`, outlined round its edge. */
export function pixels(b: Bounds, ppu: number, paint: (pen: Pen) => void): Form & { kind: "pixels" } {
  // A pixel all round for the outline, and the box on the pixel grid (so its pixels land square on whatever it's baked into).
  const box = { minX: (Math.floor(b.minX * ppu) - 1) / ppu, minY: (Math.floor(b.minY * ppu) - 1) / ppu, maxX: (Math.ceil(b.maxX * ppu) + 1) / ppu, maxY: (Math.ceil(b.maxY * ppu) + 1) / ppu };
  return {
    kind: "pixels",
    ppu,
    box,
    paint: (ctx, palette) => {
      paint(new Pen(ctx, box, ppu, palette));
      outline(ctx.canvas);
    },
  };
}

/** The picture for a pixel form in `palette`, painted the first time it's asked for. */
export function pixelPicture(form: Form & { kind: "pixels" }, palette: Palette): HTMLCanvasElement {
  let byPalette = painted.get(form);
  if (!byPalette) painted.set(form, (byPalette = new Map()));
  const key = keyOf(palette);
  let c = byPalette.get(key);
  if (!c) {
    c = document.createElement("canvas");
    c.width = Math.max(1, Math.round((form.box.maxX - form.box.minX) * form.ppu));
    c.height = Math.max(1, Math.round((form.box.maxY - form.box.minY) * form.ppu));
    form.paint(c.getContext("2d", { willReadFrequently: true })!, palette);
    byPalette.set(key, c);
  }
  return c;
}

// ------------------------------------------------------------ vector rigs as pixel art

let probe: CanvasRenderingContext2D | null = null;
/** A context to ask "is this point in that path?" of (nothing is drawn on it). */
function prober(): CanvasRenderingContext2D {
  return (probe ??= document.createElement("canvas").getContext("2d")!);
}

function path2d(ops: readonly PathOp[]): Path2D {
  const p = new Path2D();
  for (const op of ops) {
    if (op[0] === "M") p.moveTo(op[1], op[2]);
    else if (op[0] === "L") p.lineTo(op[1], op[2]);
    else if (op[0] === "Q") p.quadraticCurveTo(op[1], op[2], op[3], op[4]);
    else if (op[0] === "C") p.bezierCurveTo(op[1], op[2], op[3], op[4], op[5], op[6]);
    else p.closePath();
  }
  return p;
}

/** Where a rig shape is, as a region in its part's units. */
function insideOf(s: Shape): Inside {
  const [ox, oy] = s.at ?? [0, 0];
  const f = s.form;
  if (f.kind === "disc") {
    const c = Math.cos(-(f.rot ?? 0));
    const n = Math.sin(-(f.rot ?? 0));
    const ry = f.ry ?? f.r;
    return (x, y) => {
      const u = (x - ox) * c - (y - oy) * n;
      const v = (x - ox) * n + (y - oy) * c;
      return (u / f.r) ** 2 + (v / ry) ** 2 <= 1;
    };
  }
  if (f.kind === "capsule") return cap(ox, oy, ox + f.to[0], oy + f.to[1], f.width);
  if (f.kind === "path") {
    const p = path2d(f.ops);
    return (x, y) => prober().isPointInPath(p, x - ox, y - oy);
  }
  if (f.kind === "stroke") {
    const p = path2d(f.ops);
    return (x, y) => {
      const g = prober();
      g.lineWidth = f.width;
      g.lineCap = "round";
      g.lineJoin = "round";
      return g.isPointInStroke(p, x - ox, y - oy);
    };
  }
  return () => false;
}

/**
 * A vector rig redrawn as pixel art at `ppu` pixels a unit: each part's
 * shapes painted once, flat in their own colours, the part lit from the
 * upper left and outlined like the tiles. Glowing shapes stay as they are
 * (they're light, not paint).
 */
export function pixelRig(rig: Rig, ppu: number): Rig {
  const glow = new Set(rig.glow ?? []);
  return { ...rig, parts: rig.parts.map((p) => pixelPart(p, glow, ppu)) };
}

function pixelPart(part: Part, glow: ReadonlySet<string>, ppu: number): Part {
  const plain = part.shapes.filter((s) => !glow.has(s.color));
  if (!plain.length) return part;
  const b = emptyBounds();
  for (const s of plain) union(b, shapeBounds(s));
  if (isEmpty(b)) return part;
  const art: Shape = {
    color: "",
    form: {
      ...pixels(b, ppu, (pen) => {
        for (const s of plain) pen.shape(s);
        pen.shade();
      }),
      from: plain,
    },
  };
  return { ...part, shapes: [art, ...part.shapes.filter((s) => glow.has(s.color))] };
}

/** A vector rig as pixel art (see pixelRig), checked and ready to draw; at the tiles' own 32 pixels a block unless told otherwise. */
export const pixelModel = (rig: Rig, ppu = 32): RigModel => new RigModel(pixelRig(rig, ppu));
